// The palette's small icons (mockup 07). The featured types have the mockup's own drawings;
// the others reuse the nearest featured icon's look (a pale line, a grey line, a dashed box…)
// in the mockup's colours, so nothing new is drawn.

/** Icon colours from mockup 07. */
const I = {
  thermo: '#B9B4AA',
  grey: '#6B675F',
  renban: '#B48BE0',
  german: '#4FB868',
  dutch: '#E39A4B',
  pale: '#9A958C',
  region: '#4E86D8',
  cage: '#3A3833',
  ink: '#1F1E1C',
  shape: '#D3CEC4',
};

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="shrink-0">
    {children}
  </svg>
);

const zigzag = (colour: string) => (
  <Svg>
    <path
      d="M2 13L6 5L10 13L14 5"
      fill="none"
      stroke={colour}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const flat = (colour: string, width = 4) => (
  <Svg>
    <path d="M3 9H15" stroke={colour} strokeWidth={width} strokeLinecap="round" />
  </Svg>
);

const outsideBox = (label: string) => (
  <Svg>
    <rect x="6" y="5" width="10.5" height="8" fill="none" stroke={I.pale} strokeWidth="1.2" />
    <text
      x="0.5"
      y="12"
      fontFamily="Inter, sans-serif"
      fontSize="7.5"
      fontWeight="700"
      fill={I.cage}
    >
      {label}
    </text>
  </Svg>
);

export function ItemIcon({ id }: { id: string }) {
  switch (id) {
    case 'thermo':
    case 'slow-thermo':
      return (
        <Svg>
          <path d="M5 13L15 3" stroke={I.thermo} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="5" cy="13" r="3.8" fill={I.thermo} />
        </Svg>
      );
    case 'arrow':
      return (
        <Svg>
          <circle cx="5" cy="13" r="3.5" fill="none" stroke={I.grey} strokeWidth="1.5" />
          <path
            d="M7.5 10.5L15 3M11 3h4v4"
            fill="none"
            stroke={I.grey}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case 'renban':
    case 'nabner':
      return flat(I.renban);
    case 'german-whispers':
      return zigzag(I.german);
    case 'dutch-whispers':
    case 'whispers':
      return zigzag(I.dutch);
    case 'palindrome':
    case 'zipper':
    case 'entropic':
    case 'modular':
    case 'parity':
      return (
        <Svg>
          <path
            d="M3 13C6 3 12 3 15 13"
            fill="none"
            stroke={I.pale}
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </Svg>
      );
    case 'region-sum':
      return (
        <Svg>
          <path
            d="M2 12L8 6L16 10"
            fill="none"
            stroke={I.region}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case 'between':
    case 'lockout':
    case 'double-arrow':
      return (
        <Svg>
          <path d="M6.6 9H11.4" stroke={I.pale} strokeWidth="1.6" />
          <circle cx="4" cy="9" r="2.6" fill="none" stroke={I.grey} strokeWidth="1.4" />
          <circle cx="14" cy="9" r="2.6" fill="none" stroke={I.grey} strokeWidth="1.4" />
        </Svg>
      );
    case 'killer':
    case 'extra-region':
      return (
        <Svg>
          <rect
            x="2.5"
            y="2.5"
            width="13"
            height="13"
            fill="none"
            stroke={I.cage}
            strokeWidth="1.2"
            strokeDasharray="2 1.6"
          />
        </Svg>
      );
    case 'little-killer':
      return (
        <Svg>
          <path
            d="M3 3L15 15M11 15h4v-4"
            fill="none"
            stroke={I.cage}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case 'sandwich':
      return outsideBox('7');
    case 'x-sum':
      return outsideBox('X');
    case 'skyscraper':
      return outsideBox('3');
    case 'frame-sum':
    case 'battlefield':
      return outsideBox('Σ');
    case 'kropki':
    case 'difference':
    case 'ratio':
      return (
        <Svg>
          <circle cx="5.5" cy="9" r="3" fill="#FFFFFF" stroke={I.ink} strokeWidth="1.3" />
          <circle cx="12.5" cy="9" r="3" fill={I.ink} />
        </Svg>
      );
    case 'xv':
    case 'edge-sum':
      return (
        <Svg>
          <text
            x="9"
            y="13"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="10"
            fontWeight="700"
            fill={I.ink}
          >
            XV
          </text>
        </Svg>
      );
    case 'greater-than':
      return (
        <Svg>
          <path
            d="M6 4L12 9L6 14"
            fill="none"
            stroke={I.ink}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case 'quadruple':
      return (
        <Svg>
          <circle cx="9" cy="9" r="6.5" fill="#FFFFFF" stroke={I.ink} strokeWidth="1.3" />
          <text
            x="9"
            y="11"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="5.5"
            fontWeight="700"
            fill={I.ink}
          >
            12
          </text>
        </Svg>
      );
    case 'odd-even':
    case 'min-max':
    case 'fortress':
      return (
        <Svg>
          <circle cx="5.5" cy="9" r="3.6" fill={I.shape} />
          <rect x="10" y="5.4" width="7.2" height="7.2" fill={I.shape} />
        </Svg>
      );
    case 'irregular':
    case 'magic-square':
    case 'clone':
      return (
        <Svg>
          <path
            d="M3 3H10V8H15V15H3Z"
            fill="none"
            stroke={I.ink}
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case 'anti-knight':
    case 'anti-king':
    case 'non-consecutive':
    case 'disjoint-groups':
      return (
        <Svg>
          <path
            d="M5 14V6H12"
            fill="none"
            stroke={I.grey}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="5" cy="14" r="1.8" fill={I.grey} />
          <circle cx="12" cy="6" r="1.8" fill={I.grey} />
        </Svg>
      );
    case 'diagonal':
      return (
        <Svg>
          <path
            d="M3 15L15 3"
            stroke={I.grey}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeDasharray="2 2"
          />
        </Svg>
      );
    default:
      return flat(I.pale, 2.6);
  }
}
